import { useMemo } from 'react';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetLogisticOperatorByIdQuery } from '../services/logistic-operators.service';
import type { LogisticOperator } from '../models';
import { LogisticOperatorStatus } from '../enums';
import {
  formatLogisticOperatorBusinessDisplayLabel,
  formatLogisticOperatorNameDisplayLabel,
} from '../utils/logisticOperatorSuggestionLabel';

interface UseLogisticOperatorsSearchBarSelectedOptionsParams {
  nameId: string;
  businessId: string;
}

const useLogisticOperatorsSearchBarSelectedOptions = ({
  nameId,
  businessId,
}: UseLogisticOperatorsSearchBarSelectedOptionsParams) => {
  const { data: nameOperatorDetails, isFetching: isNameDetailsFetching } =
    useGetLogisticOperatorByIdQuery(
      { logisticOperatorId: nameId },
      { skip: !nameId }
    );

  const { data: businessOperatorDetails, isFetching: isBusinessDetailsFetching } =
    useGetLogisticOperatorByIdQuery(
      { logisticOperatorId: businessId },
      { skip: !businessId }
    );

  const selectedNameOption = useMemo((): Option | null => {
    if (!nameId) return null;
    if (nameOperatorDetails) {
      return {
        label: formatLogisticOperatorNameDisplayLabel(nameOperatorDetails),
        data: nameOperatorDetails,
      };
    }
    const placeholder: LogisticOperator = {
      id: nameId,
      fullName: '',
      businessName: '',
      status: LogisticOperatorStatus.CREATED,
    };
    if (isNameDetailsFetching) {
      return { label: 'Cargando…', data: placeholder };
    }
    return {
      label: formatLogisticOperatorNameDisplayLabel(placeholder),
      data: placeholder,
    };
  }, [isNameDetailsFetching, nameId, nameOperatorDetails]);

  const selectedBusinessOption = useMemo((): Option | null => {
    if (!businessId) return null;
    if (businessOperatorDetails) {
      return {
        label: formatLogisticOperatorBusinessDisplayLabel(
          businessOperatorDetails
        ),
        data: businessOperatorDetails,
      };
    }
    const placeholder: LogisticOperator = {
      id: businessId,
      fullName: '',
      businessName: '',
      status: LogisticOperatorStatus.CREATED,
    };
    if (isBusinessDetailsFetching) {
      return { label: 'Cargando…', data: placeholder };
    }
    return {
      label: formatLogisticOperatorBusinessDisplayLabel(placeholder),
      data: placeholder,
    };
  }, [businessId, businessOperatorDetails, isBusinessDetailsFetching]);

  return { selectedNameOption, selectedBusinessOption };
};

export default useLogisticOperatorsSearchBarSelectedOptions;
